/**
 * Хранение пары токенов.
 *
 * Отличие от sharing-admin: там `localStorage` и синхронный доступ. Здесь
 * `@capacitor/preferences` — на Android это SharedPreferences (а не файл в
 * WebView-профиле, который чистится вместе с кэшем), в браузере тот же
 * localStorage. API асинхронный, поэтому пара держится ещё и в памяти:
 * `readTokens()` остаётся синхронным для вызывающих, а `hydrateTokens()`
 * один раз наполняет кэш на старте приложения.
 *
 * ⚠ Refresh-токен — учётные данные. Не логировать, не класть в span, не
 * прикреплять к ошибке.
 */
import { Preferences } from '@capacitor/preferences'

const ACCESS = 'aida.scout.access'
const REFRESH = 'aida.scout.refresh'
const EXPIRES = 'aida.scout.expires'

export type TokenPair = {
  accessToken: string
  refreshToken: string
  /** RFC3339, время истечения access-токена. Пустая строка = не задано. */
  expiresAt: string
}

let cache: TokenPair | null = null
let hydrated = false

/** Наполняет синхронный кэш из постоянного хранилища. Вызывается один раз в main.tsx. */
export async function hydrateTokens(): Promise<TokenPair | null> {
  const [a, r, e] = await Promise.all([
    Preferences.get({ key: ACCESS }),
    Preferences.get({ key: REFRESH }),
    Preferences.get({ key: EXPIRES }),
  ])
  hydrated = true
  if (!a.value || !r.value) {
    cache = null
    return null
  }
  cache = { accessToken: a.value, refreshToken: r.value, expiresAt: e.value ?? '' }
  return cache
}

/** true, если hydrateTokens уже отработал. До этого readTokens врёт «нет сессии». */
export function isHydrated(): boolean {
  return hydrated
}

export function readTokens(): TokenPair | null {
  return cache
}

export function writeTokens(p: TokenPair): void {
  cache = p
  // Пишем в фоне: вызывающему важен кэш, а не факт записи на диск.
  void Preferences.set({ key: ACCESS, value: p.accessToken })
  void Preferences.set({ key: REFRESH, value: p.refreshToken })
  void Preferences.set({ key: EXPIRES, value: p.expiresAt ?? '' })
}

export function clearTokens(): void {
  cache = null
  void Preferences.remove({ key: ACCESS })
  void Preferences.remove({ key: REFRESH })
  void Preferences.remove({ key: EXPIRES })
}

export type Claims = {
  sub: string
  roles: string[]
  exp: number
  iat: number
  jti: string
}

export function decodeClaims(token: string): Claims | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    const bytes = Uint8Array.from(json, (c) => c.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes)) as Claims
  } catch {
    return null
  }
}

/** Протухшим считаем за 60 с до формального exp — чтобы не ловить 401 на границе. */
export function isExpired(token: string, skewSec = 60): boolean {
  const c = decodeClaims(token)
  if (!c?.exp) return true
  return c.exp * 1000 - skewSec * 1000 <= Date.now()
}

/** Только для тестов. */
export function __setTokensForTest(p: TokenPair | null): void {
  cache = p
  hydrated = true
}
